<template>
  <SettingsPageLayout>
    <HFetchQuery :query="budgetsQuery">
      <template #default="{ data }">
        <PendingChangeRequestAlerts
          :pending-change-requests="pendingChangeRequests?.records || []"
          :total-spend="getTotalSpend(data.active_budget)"
        />
        <div v-if="!isLoadingPendingChangeRequests && !hasPendingChangeRequest" class="flex justify-end mb-2">
          <Button variant="outline" @click="showAdvertisingChangeRequest = true">
            Advertising Change Request
            <vue-feather type="plus-circle" size="20" />
          </Button>
        </div>
        <div class="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          <PlatformBudgetCard
            name="Indeed"
            :spend="data.active_budget?.indeed_spend ?? 0"
            description="Indeed, Inc. is an American worldwide employment website for job listings launched in November 2004. It is a subsidiary of Japan's Recruit Holdings and is headquartered in Austin, Texas, and Stamford, Connecticut, with additional offices around the world."
          />
          <PlatformBudgetCard
            name="ZipRecruiter"
            :spend="data.active_budget?.zip_spend ?? 0"
            description="ZipRecruiter is an American employment marketplace for job seekers and employers. The company is headquartered in Santa Monica, California with offices in Tempe, AZ; London, UK and Tel Aviv, Israel."
          />
          <PlatformBudgetCard
            name="Monster"
            :spend="data.active_budget?.monster_spend ?? 0"
            description="Monster.com is a global employment website that was established in 1999 as a result of the merger between The Monster Board and Online Career Centre. It is a subsidiary of Randstad Holding, a Dutch multinational human resource consulting firm. The company's headquarters are in Weston, Massachusetts."
          />
          <PlatformBudgetCard
            v-if="(data.active_budget?.meta_spend ?? 0) > 0"
            name="Meta"
            :spend="data.active_budget?.meta_spend ?? 0"
            description="Meta Platforms, Inc., doing business as Meta, and formerly named Facebook, Inc., and TheFacebook, Inc., is an American multinational technology conglomerate based in Menlo Park, California. The company owns and operates Facebook, Instagram, Threads, and WhatsApp, among other products and services."
          />
        </div>

        <AdvertisingHistory class="mt-6" :history="getBudgetHistory(data.records)" />

        <AdvertisingChangeRequest
          v-if="showAdvertisingChangeRequest"
          :current-total-spend="getTotalSpend(data.active_budget)"
          @handle-cancel="showAdvertisingChangeRequest = false"
          @handle-save="
            showAdvertisingChangeRequest = false;
            reloadPendingChangeRequests();
          "
        />
      </template>
    </HFetchQuery>
  </SettingsPageLayout>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import SettingsPageLayout from '../../../layouts/SettingsPageLayout/index.vue';
import { Button } from '@/components/ui/button';
import AdvertisingChangeRequest from './AdvertisingChangeRequest.vue';
import AdvertisingHistory, { type AdvertisingHistoryRow } from './AdvertisingHistory.vue';
import PendingChangeRequestAlerts from './PendingChangeRequestAlerts.vue';
import PlatformBudgetCard from './PlatformBudgetCard.vue';
import { Api } from '@/api';
import HFetchQuery from '@/lib/hfetch/HFetchQuery.vue';
import { useHFetchQuery } from '@/lib/hfetch/useHFetchQuery';
import type { MonthlyMarketingBudgetEntity } from '@/api/entity_types/MonthlyMarketingBudget';

const showAdvertisingChangeRequest = ref(false);

const budgetsQuery = useHFetchQuery(() => Api.MonthlyMarketingBudgets.listMonthlyMarketingBudgets());

const {
  data: pendingChangeRequests,
  isLoading: isLoadingPendingChangeRequests,
  reload: reloadPendingChangeRequests,
} = useHFetchQuery(() => Api.MonthlyMarketingBudgetUpdateRequests.listBudgetUpdateRequests({ status: 'pending' }));

const hasPendingChangeRequest = computed(() => (pendingChangeRequests.value?.records.length ?? 0) > 0);

const getTotalSpend = (budget: MonthlyMarketingBudgetEntity | null) => {
  if (budget == null) return 0;
  return (budget.indeed_spend ?? 0) + (budget.zip_spend ?? 0) + (budget.monster_spend ?? 0) + (budget.meta_spend ?? 0);
};

const getBudgetHistory = (records: MonthlyMarketingBudgetEntity[]): AdvertisingHistoryRow[] =>
  records.map(b => ({
    start_date: b.start_date,
    end_date: b.end_date,
    indeed_spend: b.indeed_spend,
    zip_spend: b.zip_spend,
    monster_spend: b.monster_spend,
    meta_spend: b.meta_spend,
    total_spend: getTotalSpend(b),
  }));
</script>
